import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InternalLinking = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [recommendations, setRecommendations] = useState({ recommended: [], implemented: [] });
  const [orphans, setOrphans] = useState([]);
  const [flowMap, setFlowMap] = useState([]);
  const [anchorReport, setAnchorReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLink, setNewLink] = useState({ from_page: '', to_page: '', anchor_text: '', notes: '' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, recRes, orphanRes, flowRes, anchorRes] = await Promise.all([
        fetch(`${API_URL}/api/internal-links/dashboard`),
        fetch(`${API_URL}/api/internal-links/recommendations`),
        fetch(`${API_URL}/api/internal-links/orphan-pages`),
        fetch(`${API_URL}/api/internal-links/link-juice-flow`),
        fetch(`${API_URL}/api/internal-links/anchor-text-report`)
      ]);
      if (dashRes.ok) setDashboard(await dashRes.json());
      if (recRes.ok) setRecommendations(await recRes.json());
      if (orphanRes.ok) setOrphans((await orphanRes.json()).orphan_pages || []);
      if (flowRes.ok) setFlowMap((await flowRes.json()).flow_map || []);
      if (anchorRes.ok) setAnchorReport((await anchorRes.json()).report || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateStatus = async (id, status) => {
    await fetch(`${API_URL}/api/internal-links/recommendation/${id}?status=${status}`, { method: 'PUT' });
    fetchData();
  };

  const addRecommendation = async () => {
    if (!newLink.from_page || !newLink.to_page || !newLink.anchor_text) return;
    await fetch(`${API_URL}/api/internal-links/recommendation?from_page=${encodeURIComponent(newLink.from_page)}&to_page=${encodeURIComponent(newLink.to_page)}&anchor_text=${encodeURIComponent(newLink.anchor_text)}&notes=${encodeURIComponent(newLink.notes)}`, { method: 'POST' });
    setNewLink({ from_page: '', to_page: '', anchor_text: '', notes: '' });
    fetchData();
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'recommendations', label: 'Link Recommendations', icon: '🔗' },
    { id: 'orphans', label: 'Orphan Pages', icon: '🏝️' },
    { id: 'flow', label: 'Link Flow', icon: '🌊' },
    { id: 'anchors', label: 'Anchor Text', icon: '⚓' },
    { id: 'add', label: 'Add Link', icon: '➕' },
  ];

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet><title>Internal Linking | Admin</title></Helmet>
      
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6">
        <h1 className="text-2xl font-bold">🔗 Internal Linking Optimization</h1>
        <p className="text-indigo-100">Optimize your site's link structure for better SEO</p>
      </div>

      <div className="p-6">
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg whitespace-nowrap ${activeTab === tab.id ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* DASHBOARD */}
        {activeTab === 'dashboard' && dashboard && (
          <div className="space-y-6">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <h3 className="font-semibold text-indigo-800">📈 Internal Linking Benefits</h3>
              <p className="text-indigo-700 text-sm mt-1">Strategic internal linking distributes page authority, helps search engines discover content, and improves user navigation.</p>
            </div>

            <div className="grid md:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-lg shadow text-center border-l-4 border-blue-500">
                <p className="text-gray-500 text-sm">Total Links</p>
                <p className="text-2xl font-bold text-blue-600">{dashboard.stats.total_recommendations}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow text-center border-l-4 border-green-500">
                <p className="text-gray-500 text-sm">Implemented</p>
                <p className="text-2xl font-bold text-green-600">{dashboard.stats.implemented}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow text-center border-l-4 border-orange-500">
                <p className="text-gray-500 text-sm">Pending</p>
                <p className="text-2xl font-bold text-orange-600">{dashboard.stats.pending}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow text-center border-l-4 border-purple-500">
                <p className="text-gray-500 text-sm">Completion</p>
                <p className="text-2xl font-bold text-purple-600">{dashboard.stats.completion_rate}%</p>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b"><h3 className="font-semibold">📄 Key Pages Overview</h3></div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Page</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Priority</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Outgoing</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Incoming</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {dashboard.key_pages.map(page => (
                      <tr key={page.url} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <span className="font-medium">{page.title}</span>
                          <span className="text-gray-500 text-sm ml-2">{page.url}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {page.priority >= 5 ? '🔴' : page.priority >= 4 ? '🟠' : page.priority >= 3 ? '🟡' : '⚪'}
                        </td>
                        <td className="px-4 py-3 text-center font-medium">{dashboard.link_map[page.url]?.outgoing || 0}</td>
                        <td className="px-4 py-3 text-center font-medium">{dashboard.link_map[page.url]?.incoming || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* RECOMMENDATIONS */}
        {activeTab === 'recommendations' && (
          <div className="space-y-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="font-semibold text-yellow-800">🔗 Link Recommendations</h3>
              <p className="text-yellow-700 text-sm mt-1">Implement these internal links to improve your site structure. Mark as "Implemented" once you've added the link to your page.</p>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b"><h3 className="font-semibold">Pending Recommendations ({recommendations.recommended?.length || 0})</h3></div>
              <div className="divide-y">
                {recommendations.recommended?.map(link => (
                  <div key={link.id} className="p-4 flex justify-between items-center">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-indigo-600">{link.from_page}</span>
                        <span className="text-gray-400">→</span>
                        <span className="font-medium text-green-600">{link.to_page}</span>
                      </div>
                      <p className="text-sm text-gray-500">Anchor: "{link.anchor_text}"</p>
                      {link.notes && <p className="text-xs text-gray-400 mt-1">💡 {link.notes}</p>}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => updateStatus(link.id, 'implemented')} className="bg-green-100 text-green-700 px-3 py-1 rounded text-sm hover:bg-green-200">✓ Done</button>
                      <button onClick={() => updateStatus(link.id, 'rejected')} className="bg-red-100 text-red-700 px-3 py-1 rounded text-sm hover:bg-red-200">✕ Skip</button>
                    </div>
                  </div>
                ))}
                {(!recommendations.recommended || recommendations.recommended.length === 0) && (
                  <div className="p-8 text-center text-gray-500">🎉 All recommendations implemented!</div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b"><h3 className="font-semibold text-green-700">✓ Implemented ({recommendations.implemented?.length || 0})</h3></div>
              <div className="divide-y max-h-64 overflow-y-auto">
                {recommendations.implemented?.map(link => (
                  <div key={link.id} className="p-3 flex justify-between items-center text-sm">
                    <span><span className="text-indigo-600">{link.from_page}</span> → <span className="text-green-600">{link.to_page}</span></span>
                    <span className="text-gray-400">"{link.anchor_text}"</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ORPHAN PAGES */}
        {activeTab === 'orphans' && (
          <div className="space-y-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="font-semibold text-red-800">🏝️ Orphan Pages</h3>
              <p className="text-red-700 text-sm mt-1">These pages have few or no incoming internal links. Search engines may have difficulty finding and ranking them.</p>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b"><h3 className="font-semibold">Pages Needing More Links</h3></div>
              <div className="divide-y">
                {orphans.map(page => (
                  <div key={page.url} className="p-4 flex justify-between items-center">
                    <div>
                      <span className="font-medium">{page.title}</span>
                      <span className="text-gray-500 text-sm ml-2">{page.url}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500">{page.incoming_links} incoming links</span>
                      <span className={`px-2 py-1 rounded text-xs ${page.status === 'critical' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {page.status === 'critical' ? '⚠️ Critical' : '⚡ Warning'}
                      </span>
                    </div>
                  </div>
                ))}
                {orphans.length === 0 && <div className="p-8 text-center text-gray-500">✅ No orphan pages found!</div>}
              </div>
            </div>
          </div>
        )}

        {/* LINK FLOW */}
        {activeTab === 'flow' && (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-800">🌊 Link Equity Flow</h3>
              <p className="text-blue-700 text-sm mt-1">See how link equity (PageRank) flows through your site. Pages with higher juice scores pass more authority.</p>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Page</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Incoming</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Outgoing</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Juice Score</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {flowMap.map((page, i) => (
                    <tr key={page.page} className={i < 3 ? 'bg-green-50' : ''}>
                      <td className="px-4 py-3">
                        <span className="font-medium">{page.title}</span>
                        {i < 3 && <span className="ml-2 text-xs text-green-600">🏆 Top {i + 1}</span>}
                      </td>
                      <td className="px-4 py-3 text-center font-medium text-blue-600">{page.incoming}</td>
                      <td className="px-4 py-3 text-center font-medium text-orange-600">{page.outgoing}</td>
                      <td className="px-4 py-3 text-center font-bold text-purple-600">{page.juice_score}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`font-medium ${page.balance > 0 ? 'text-green-600' : page.balance < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                          {page.balance > 0 ? '+' : ''}{page.balance}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ANCHOR TEXT */}
        {activeTab === 'anchors' && (
          <div className="space-y-6">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h3 className="font-semibold text-purple-800">⚓ Anchor Text Analysis</h3>
              <p className="text-purple-700 text-sm mt-1">Diverse anchor text is important for SEO. Avoid over-optimizing with the same exact keywords.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {anchorReport.map(page => (
                <div key={page.page} className="bg-white rounded-lg shadow">
                  <div className="p-4 border-b flex justify-between items-center">
                    <span className="font-medium">{page.title}</span>
                    <span className={`px-2 py-1 rounded text-xs ${page.status === 'good' ? 'bg-green-100 text-green-700' : page.status === 'warning' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                      {page.variety_score}% variety
                    </span>
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-gray-500 mb-2">{page.total_links} links, {page.unique_anchors} unique anchors</p>
                    <div className="flex flex-wrap gap-1">
                      {page.anchors.slice(0, 5).map((anchor, i) => (
                        <span key={i} className="text-xs bg-gray-100 px-2 py-1 rounded">{anchor}</span>
                      ))}
                    </div>
                    <p className="text-xs text-gray-400 mt-2">Target: {page.target_keywords.slice(0, 2).join(', ')}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ADD LINK */}
        {activeTab === 'add' && (
          <div className="max-w-xl bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold text-gray-800 mb-4">➕ Add Custom Link Recommendation</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Page</label>
                <select value={newLink.from_page} onChange={e => setNewLink({...newLink, from_page: e.target.value})} className="w-full border rounded px-3 py-2">
                  <option value="">Select page...</option>
                  {dashboard?.key_pages.map(p => <option key={p.url} value={p.url}>{p.title} ({p.url})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To Page</label>
                <select value={newLink.to_page} onChange={e => setNewLink({...newLink, to_page: e.target.value})} className="w-full border rounded px-3 py-2">
                  <option value="">Select page...</option>
                  {dashboard?.key_pages.map(p => <option key={p.url} value={p.url}>{p.title} ({p.url})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Anchor Text</label>
                <input placeholder="e.g., dental implants specialist" value={newLink.anchor_text} onChange={e => setNewLink({...newLink, anchor_text: e.target.value})} className="w-full border rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                <input placeholder="Why this link?" value={newLink.notes} onChange={e => setNewLink({...newLink, notes: e.target.value})} className="w-full border rounded px-3 py-2" />
              </div>
              <button onClick={addRecommendation} disabled={!newLink.from_page || !newLink.to_page || !newLink.anchor_text} className="w-full bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 disabled:opacity-50">Add Recommendation</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InternalLinking;
